/**
 * Running Tasks Panel Component
 *
 * Displays currently running task executions with:
 * - Progress bar showing completion percentage
 * - Record counts (processed/total)
 * - Start time and estimated completion
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRunningExecutions, cancelSqlSync, type TaskExecution } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, PlayCircle, XCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface RunningTasksPanelProps {
  className?: string;
}

export function RunningTasksPanel({ className }: RunningTasksPanelProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch running executions with fast polling when there are running tasks
  const { data, isLoading } = useQuery({
    queryKey: ['runningExecutions'],
    queryFn: getRunningExecutions,
    refetchInterval: (query) => {
      // Poll every 5 seconds if there are running tasks, otherwise every 30 seconds
      const hasRunning = query.state.data?.executions && query.state.data.executions.length > 0;
      return hasRunning ? 5000 : 30000;
    }
  });

  // Cancel mutation for SQL Sync jobs
  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => cancelSqlSync(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['runningExecutions'] });
      queryClient.invalidateQueries({ queryKey: ['sqlSyncDashboard'] });
      toast({
        title: 'Job Cancelled',
        description: 'The sync job has been cancelled',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: `Failed to cancel job: ${error.message}`,
        variant: 'destructive'
      });
    }
  });

  const runningTasks = data?.executions || [];

  // Don't render anything if no running tasks and not loading
  if (!isLoading && runningTasks.length === 0) {
    return null;
  }

  // Calculate progress percentage
  const getProgressPercent = (execution: TaskExecution): number => {
    if (!execution.recordsFound || execution.recordsFound === 0) return 0;
    const processed = execution.recordsProcessed || 0;
    return Math.min(100, Math.round((processed / execution.recordsFound) * 100));
  };

  // Format time elapsed
  const formatTimeElapsed = (startTime: string): string => {
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffSecs = Math.floor(diffMs / 1000);

    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  // Estimate remaining time based on current progress
  const estimateRemaining = (execution: TaskExecution): string | null => {
    if (!execution.recordsFound || !execution.recordsProcessed || execution.recordsProcessed === 0) {
      return null;
    }

    const start = new Date(execution.startTime);
    const now = new Date();
    const elapsedMs = now.getTime() - start.getTime();

    const processedRecords = execution.recordsProcessed;
    const remainingRecords = execution.recordsFound - processedRecords;

    if (remainingRecords <= 0) return 'completing...';

    const msPerRecord = elapsedMs / processedRecords;
    const remainingMs = msPerRecord * remainingRecords;

    const remainingSecs = Math.ceil(remainingMs / 1000);
    if (remainingSecs < 60) return `~${remainingSecs}s remaining`;
    const remainingMins = Math.ceil(remainingSecs / 60);
    if (remainingMins < 60) return `~${remainingMins}m remaining`;
    const remainingHours = Math.floor(remainingMins / 60);
    return `~${remainingHours}h ${remainingMins % 60}m remaining`;
  };

  // Get progress bar color based on status
  const getProgressColor = (execution: TaskExecution): string => {
    if (execution.recordsFailed && execution.recordsFailed > 0) {
      return 'bg-yellow-500'; // Some failures
    }
    return 'bg-blue-500';
  };

  return (
    <Card className={cn("mb-4", className)}>
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-green-500" />
          Currently Running ({runningTasks.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-16">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {runningTasks.map((execution) => {
              const progress = getProgressPercent(execution);
              const timeElapsed = formatTimeElapsed(execution.startTime);
              const remaining = estimateRemaining(execution);

              return (
                <div key={execution.executionId} className="space-y-2">
                  {/* Task Name & Stats Row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                      <span className="font-medium text-sm">
                        {execution.taskName || 'Unknown Task'}
                      </span>
                      {/* Cancel button */}
                      {execution.jobId && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => cancelMutation.mutate(execution.jobId!)}
                                disabled={cancelMutation.isPending}
                              >
                                <XCircle className="h-4 w-4 text-orange-500 hover:text-orange-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Cancel this sync job</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {execution.recordsProcessed?.toLocaleString() || 0}
                      {execution.recordsFound ? `/${execution.recordsFound.toLocaleString()}` : ''} records
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        "h-full transition-all duration-500",
                        getProgressColor(execution)
                      )}
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {/* Time Info Row */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Started {timeElapsed}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{progress}%</span>
                      {remaining && <span>{remaining}</span>}
                    </div>
                  </div>

                  {/* Error Count (if any) */}
                  {execution.recordsFailed != null && execution.recordsFailed > 0 && (
                    <div className="text-xs text-yellow-600 dark:text-yellow-500">
                      {execution.recordsFailed} record{execution.recordsFailed !== 1 ? 's' : ''} failed
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
